import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useState } from "react";
import { toast } from "react-toastify";
import { TrashIcon } from "@heroicons/react/24/outline";
import { useAuthStore } from "../../../store/authStore";
import {
  PrimaryCTAButton,
  SecondaryCTAButton,
} from "../../../components/CTAButtonComponents";
import { Select } from "../../../components/inputComponent";
import { TableData } from "../../../utils/Utility";
import { SwitchButton } from "../../../components/SwitchButton";
import {
  useGetCompanyUsers,
  usecompanyAccess,
  useRemoveUserAccess,
} from "../../../queries/companyStats/captableStats";

type Props = {
  isPopupOpen: boolean;
  onClose: () => void;
  userList: any;
  onUserAdded?: (companyId: string, user: TableData) => void;
  onUserRemoved?: (companyId: string, user: TableData) => void;
};

const normalizeEmail = (value?: string) => value?.trim().toLowerCase() || "";

// Delete confirmation popup component
interface DeleteConfirmationProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  userEmail: string;
  isConfirming: boolean;
}

const DeleteConfirmationPopup: React.FC<DeleteConfirmationProps> = ({
  isOpen,
  onClose,
  onConfirm,
  userEmail,
  isConfirming,
}) => {
  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <TrashIcon
                      className="h-6 w-6 text-red-600"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <Dialog.Title
                      as="h3"
                      className="text-base font-semibold leading-6 text-gray-900"
                    >
                      Remove User Access
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to remove access for{" "}
                        <strong>{userEmail}</strong>? This user will no longer
                        be able to access this company&apos;s data.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 flex justify-end flex-row gap-3 reverse">
                  <PrimaryCTAButton
                    onClick={onConfirm}
                    disabled={isConfirming}
                    className="inline-flex w-auto justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:w-auto"
                  >
                    Remove Access
                  </PrimaryCTAButton>
                  <SecondaryCTAButton
                    onClick={onClose}
                    className="mt-3 inline-flex w-auto justify-center rounded-md bg-white px-2 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  >
                    Cancel
                  </SecondaryCTAButton>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
function UserAccessPopup({
  isPopupOpen,
  onClose,
  userList,
  onUserAdded,
  onUserRemoved,
}: Props) {
  const user = useAuthStore();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [designation, setDesignation] = useState("OTHERS");
  const [roleName, setRoleName] = useState("Admin");
  const [error, setError] = useState(false);
  const [mail, setMail] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<TableData | null>(null);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [isRemovingUser, setIsRemovingUser] = useState(false);

  const { mutate: addAccess } = usecompanyAccess();
  const { mutate: removeAccess } = useRemoveUserAccess();
  const {
    data: companyUsers,
    isLoading: isLoadingCompanyUsers,
    isError: isCompanyUsersError,
    refetch: refetchCompanyUsers,
  } = useGetCompanyUsers(userList?.id || "");
  const list: TableData[] = Array.isArray(companyUsers) ? companyUsers : [];
  function handleSubmit() {
    if (isAddingUser) return;
    if (
      fullName.length > 0 &&
      email.length > 0 &&
      designation.length > 0 &&
      roleName.length > 0
    ) {
      let roleGrants;
      const companyId = userList.id;
      if (roleName === "Admin") {
        roleGrants = [
          { role: "Admin", action: "*", resource: "*", attributes: ["*"] },
        ];
      } else if (roleName === "Admin Viewer") {
        roleGrants = [
          {
            role: "Admin Viewer",
            action: "read",
            resource: "*",
            attributes: ["*"],
          },
        ];
      }
      setError(false);
      const userObject = {
        fullName,
        email: normalizeEmail(email),
        designation,
        roleName,
        roleGrants,
        companyId,
        silentAccess: mail,
      };
      setIsAddingUser(true);
      addAccess(userObject, {
        onSuccess: (res: any) => {
          const successMsg = mail ? "Access Granted" : "Email Sent Succesfully";
          toast(successMsg, {
            type: "success",
            autoClose: 2000,
          });
          const responsePayload =
            res?.data?.data?.data || res?.data?.data || res?.data || res;
          onUserAdded?.(companyId, {
            id: responsePayload?.userId || responsePayload?.id,
            email: normalizeEmail(responsePayload?.email || userObject.email),
            fullName: responsePayload?.fullName || userObject.fullName,
            role: responsePayload?.role || roleName,
            createdAt: responsePayload?.createdAt || new Date().toISOString(),
          });
          if (!onUserAdded) {
            refetchCompanyUsers().catch(() => null);
          }
          setFullName("");
          setEmail("");
          setDesignation("OTHERS");
          setRoleName("Admin");
          setMail(false);
        },
        onError: (err: any) => {
          toast(
            err?.response?.data?.errorMessage ||
              err?.response?.data?.errors ||
              "Failed to add user access",
            {
              type: "error",
              autoClose: 2000,
            },
          );
        },
        onSettled: () => {
          setIsAddingUser(false);
        },
      });
    } else {
      setError(true);
    }
  }

  const handleDeleteUser = (userToDelete: TableData) => {
    setUserToDelete(userToDelete);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteUser = () => {
    if (isRemovingUser) return;
    if (userToDelete) {
      const deleteData = {
        companyId: userList.id,
        userId: userToDelete.id,
      };

      setIsRemovingUser(true);
      removeAccess(deleteData, {
        onSuccess: () => {
          toast("User access removed successfully", {
            type: "success",
            autoClose: 2000,
          });
          onUserRemoved?.(userList.id, userToDelete);
          if (!onUserRemoved) {
            refetchCompanyUsers().catch(() => null);
          }
          setDeleteConfirmOpen(false);
          setUserToDelete(null);
        },
        onError: () => {
          toast("Failed to remove user access", {
            type: "error",
            autoClose: 2000,
          });
          setDeleteConfirmOpen(false);
          setUserToDelete(null);
        },
        onSettled: () => {
          setIsRemovingUser(false);
        },
      });
    }
  };

  return (
    <>
      <Transition.Root show={isPopupOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                  <div
                    className={`flex p-2 ${user.role === "Admin" || user.role === "SuperAdmin" ? "justify-between" : "justify-center"}`}
                  >
                    {(user.role === "Admin" || user.role === "SuperAdmin") && (
                      <div className="sm:w-72">
                        <h1 className="font-bold text-lg">
                          Add user to company
                        </h1>
                        <form
                          className="mx-auto max-w-xl py-4 px-4"
                          action="#"
                          method="POST"
                        >
                          <div>
                            <label htmlFor="fullName" className="block text-sm">
                              FullName :
                            </label>
                            <input
                              type="text"
                              name="fullName"
                              id="fullName"
                              onChange={(e) => {
                                setFullName(e.target.value);
                              }}
                              className="block w-full rounded-md border-0 text-grey-900 placeholder:text-gray-400 ring-gray-300 ring-1 ring-inset"
                            />
                          </div>
                          <div className="py-2">
                            <label htmlFor="email" className="block text-sm">
                              Email :
                            </label>
                            <input
                              type="text"
                              name="email"
                              id="email"
                              onChange={(e) => {
                                const emailId = e.target.value.trim();
                                setEmail(emailId);
                              }}
                              className="block w-full rounded-md border-0 text-grey-900 placeholder:text-gray-400 ring-gray-300 ring-1 ring-inset"
                            />
                          </div>
                          <div>
                            <label htmlFor="roleName" className="block text-sm">
                              Role Name :
                            </label>
                            <Select
                              onChange={(e) => {
                                setRoleName(e.target.value);
                              }}
                              value={roleName}
                              options={["Admin", "Admin Viewer"]}
                            />
                          </div>
                          <div className="py-2">
                            <label
                              htmlFor="designation"
                              className="block text-sm"
                            >
                              Designation :
                            </label>
                            <Select
                              onChange={(e) => {
                                setDesignation(e.target.value);
                              }}
                              value={designation}
                              options={[
                                { value: "FOUNDER", label: "Founder" },
                                { value: "CFO", label: "CFO" },
                                { value: "DIRECTOR", label: "Director" },
                                { value: "ATTORNEY", label: "Attorney" },
                                {
                                  value: "COMPANY_SECRETARY",
                                  label: "Company Secretary",
                                },
                                {
                                  value: "CHARTED_ACCOUNTANT",
                                  label: "Chartered Accountant",
                                },
                                {
                                  value: "COMPLIANCE_OFFICER",
                                  label: "Compliance Officer",
                                },
                                { value: "CONSULTANT", label: "Consultant" },
                                { value: "OTHERS", label: "Others" },
                              ]}
                              valueGetter={(option) => option.value}
                              textGetter={(option) => option.label}
                            />
                          </div>
                          <div className="flex flex-auto">
                            <div className="p-2">Silent Access :</div>
                            <SwitchButton
                              label=""
                              value={mail}
                              onClick={() => {
                                setMail(!mail);
                              }}
                            ></SwitchButton>
                          </div>
                          {error && (
                            <p className="text-red-500 text-sm ">
                              *All Feilds are compulsory
                            </p>
                          )}
                          <div className="flex justify-between py-2">
                            <SecondaryCTAButton onClick={onClose}>
                              Cancel
                            </SecondaryCTAButton>
                            <PrimaryCTAButton
                              type="submit"
                              disabled={isAddingUser}
                              onClick={(e) => {
                                e.preventDefault();
                                handleSubmit();
                              }}
                            >
                              {isAddingUser ? "Adding..." : "Add User"}
                            </PrimaryCTAButton>
                          </div>
                        </form>
                      </div>
                    )}
                    <div>
                      <h1 className="font-bold text-lg">
                        List of Users with Access
                      </h1>
                      <div className="overflow-y-scroll h-80">
                        <ul role="list" className="divide-y divide-gray-100">
                          {isLoadingCompanyUsers && (
                            <li className="py-2">Please wait....</li>
                          )}
                          {isCompanyUsersError && (
                            <li className="py-2">
                              Could not load users.{" "}
                              <button
                                className="text-blue-500 hover:underline cursor-pointer"
                                onClick={() => refetchCompanyUsers()}
                              >
                                Retry
                              </button>
                            </li>
                          )}
                          {!isLoadingCompanyUsers &&
                            !isCompanyUsersError &&
                            list.map((userData, index: number) => (
                              <li
                                key={index}
                                className="py-2 flex items-center justify-between"
                              >
                                <h1 className="flex-1">{userData.email}</h1>
                                {(user.role === "Admin" ||
                                  user.role === "SuperAdmin") && (
                                  <button
                                    onClick={() => handleDeleteUser(userData)}
                                    className="ml-2 p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-full transition-colors"
                                    title="Remove user access"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                )}
                              </li>
                            ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Delete Confirmation Popup */}
      {deleteConfirmOpen && userToDelete && (
        <DeleteConfirmationPopup
          isOpen={deleteConfirmOpen}
          onClose={() => {
            setDeleteConfirmOpen(false);
            setUserToDelete(null);
          }}
          onConfirm={confirmDeleteUser}
          userEmail={userToDelete.email || ""}
          isConfirming={isRemovingUser}
        />
      )}
    </>
  );
}

export default UserAccessPopup;
